import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { apiRequest } from '../../config/api';
import { Shield, Mail, CheckCircle2, ArrowLeft } from 'lucide-react';

const DentalHubAdminForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: 'Error',
        description: 'Please enter your email address',
        variant: 'destructive'
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest('admin_forgot_password.php', {
        method: 'POST',
        body: JSON.stringify({
          email: email
        })
      });

      if (response.success) {
        setEmailSent(true);
        toast({
          title: 'Email Sent!',
          description: response.message || 'Password reset instructions have been sent to your email.',
        });
      } else {
        throw new Error(response.error || response.message || 'Failed to send reset email');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred. Please try again later.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <>
        <Helmet>
          <title>Email Sent - Dental Hub Admin</title>
          <meta name="description" content="Password reset email has been sent." />
        </Helmet>
        
        <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <Card className="glass-effect border-gray-700">
              <CardHeader className="text-center p-4 sm:p-6">
                <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                  <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                  <span className="text-xl sm:text-2xl font-bold gradient-text">Dental Hub Admin</span>
                </div>
                <CardTitle className="text-xl sm:text-2xl text-white">Check Your Email</CardTitle>
              </CardHeader>
              
              <CardContent className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle2 className="h-16 w-16 text-green-400" />
                </div>
                <p className="text-gray-300">
                  We've sent password reset instructions to <strong className="text-white">{email}</strong>
                </p>
                <p className="text-gray-400 text-sm">
                  Please check your email and follow the instructions to reset your password.
                  The link will expire in 1 hour.
                </p>
                
                <div className="mt-6 space-y-2">
                  <Link to="/admin/hub/login">
                    <Button 
                      variant="outline"
                      className="w-full border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                  <button
                    onClick={() => {
                      setEmailSent(false);
                      setEmail('');
                    }}
                    className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm"
                  >
                    Send another email
                  </button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Forgot Password - Dental Hub Admin</title>
        <meta name="description" content="Reset your Dental Hub admin password." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-4 sm:p-6">
              <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                <span className="text-xl sm:text-2xl font-bold gradient-text">Dental Hub Admin</span>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white">Forgot Password</CardTitle>
              <CardDescription className="text-gray-300 text-sm sm:text-base">
                Enter your email address and we'll send you instructions to reset your password
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="neinususa@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="mt-6 text-center space-y-2">
                <Link 
                  to="/admin/hub/login" 
                  className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm block"
                >
                  <ArrowLeft className="h-4 w-4 inline mr-1" />
                  Back to Login
                </Link>
                <a 
                  href="/" 
                  className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm block"
                >
                  ← Back to Home
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default DentalHubAdminForgotPasswordPage;
